import { Action, createReducer, on } from '@ngrx/store';
import { DateType, PaymentOption, ReportingConfiguration } from '@rx-applications/clientsite/shared/config/utils';
import { systemDefaultCurrency } from '../constants';
import {
  analyticsConfig,
  baseConfigActions,
  cidActions,
  clientConfig,
  envConfigActions,
  initializeCurrentCurrencySucceed,
  localeActions,
  setSelectedCurrency,
  updateRecentlyBookedHotel,
} from './config.actions';
import { ConfigState } from './config.models';
import { LoadStatus } from '@rx-applications/shared/utils';

export const CONFIG_FEATURE_KEY = 'config';

export const reportingConfigurationInitialState: ReportingConfiguration = {
  availableReports: [],
  clientFieldReports: [],
  currencyCode: '',
  dateType: DateType.CreatedOn,
};

export const baseConfigInitialState: ConfigState = {
  error: null,
  baseConfig: null,
  envConfig: {
    rxwebapi: '',
    authserver: '',
    paymentwebapi: '',
    payapi: '',
    env: '',
    features: [],
    tracking: {},
    stripe: {
      pkKey: '',
    },
    remotes: {
      'issuing-portal': '',
      'company-payment': '',
      'clientsite-auth': '',
      'clientsite-guestbook': '',
    },
    trainlinehyperlink: '',
  },
  clientConfig: {
    loadStatus: 'NOT_LOADED',
    configuration: {
      general: {
        defaultCurrency: systemDefaultCurrency,
        clientLogoUrl: '',
        firstName: '',
        lastName: '',
        fullName: '',
        emailAddress: '',
        phoneNumber: '',
        address1: '',
        address2: '',
        city: '',
        country: '',
        websiteGuid: '',
        websiteId: 0,
        emergencyPhone: false,
        enableAnalytics: false,
        enableLoyaltyPrograms: false,
        enableProFormaStatements: false,
        customHomepage: {
          enableTemplate: false,
          templateName: 'corporate',
        },
        brand: '',
        company: {
          name: '',
          plan: '',
          monthlySpend: 0,
          companyCreatedAt: '',
        },
        userHash: '',
        userCreatedAt: '',
        allowCompanyPaymentPage: false,
        allowPayPortal: false,
        hasTrainlineAccount: false,
        approvalFlowEnabled: false,
      },
      search: {
        selectedCurrency: systemDefaultCurrency,
        showPaymentOptions: false,
        defaultPaymentOption: PaymentOption.All,
        showMealOptions: false,
        defaultMealOption: 0,
        policyEnabled: false,
        hideInPolicy: false,
        topLocations: [],
        numberRecommendedHotels: 0,
        recentlyBooked: [],
        recommendLocationHeader: '',
        isCurrencyInitialize: false,
        roomSelectSortBy: 'refundableStatus',
      },
      reservation: {
        allowPayments: false,
        allowAutoCancel: false,
        disableMailingList: false,
        disableRepeatLink: false,
        showLogoOnReservationConfirmation: false,
        showClientFieldsInCustomerDetails: false,
        companyName: '',
        companyNumber: 0,
        displayRoomexCompanyDetails: false,
        displayInvoiceNumber: false,
        enableMixedSupplierResults: false,
        displayOutofHoursPhoneNumber: false,
        outofHoursPhoneNumber: '',
        enablePaxPredictive: false,
      },
      reports: reportingConfigurationInitialState,
    },
  },
  locale: '',
  cid: '',
};

const reducer = createReducer(
  baseConfigInitialState,
  on(envConfigActions.loadEnvironment, (state) => ({
    ...state,
    error: null,
  })),
  on(envConfigActions.loadEnvironmentSucceed, (state, { environmentConfiguration }) => ({
    ...state,
    envConfig: environmentConfiguration,
    error: null,
  })),
  on(envConfigActions.loadEnvironmentFailed, (state, { error }) => ({
    ...state,
    error: error.message,
  })),
  on(envConfigActions.updateEnvironmentRxwebapi, (state, { rxwebapi }) => ({
    ...state,
    envConfig: {
      ...state.envConfig,
      rxwebapi,
    },
  })),

  on(baseConfigActions.loadBaseConfig, (state) => ({ ...state, error: null })),
  on(baseConfigActions.loadBaseConfigSucceed, (state, { baseConfig }) => ({
    ...state,
    baseConfig,
    error: null,
  })),
  on(baseConfigActions.loadBaseConfigFailed, (state, { error }) => ({
    ...state,
    error: error.message,
    baseConfig: null,
  })),

  on(clientConfig.load, (state) => ({
    ...state,
    clientConfig: { ...state.clientConfig, loadStatus: 'LOADING' as LoadStatus },
  })),
  on(clientConfig.loadSuccess, (state, { configuration }) => {
    const { search, reservation, general } = configuration;
    let approvers,
      generalWithApprovers = general;
    if (general?.approvers) {
      approvers = Array.isArray(general.approvers) ? general.approvers : [general.approvers];
      generalWithApprovers = { ...general, approvers };
    }

    return {
      ...state,
      clientConfig: {
        configuration: { ...state.clientConfig.configuration, search, reservation, general: generalWithApprovers },
        loadStatus: 'LOADED' as LoadStatus,
      },
    };
  }),
  on(clientConfig.loadFailed, (state) => ({
    ...state,
    clientConfig: {
      ...state.clientConfig,
      loadStatus: 'ERROR' as LoadStatus,
    },
  })),

  on(analyticsConfig.loadSuccess, (state, { reportingConfiguration }) => {
    return {
      ...state,
      clientConfig: {
        ...state.clientConfig,
        configuration: {
          ...state.clientConfig.configuration,
          reports: reportingConfiguration,
        },
      },
    };
  }),

  on(setSelectedCurrency, initializeCurrentCurrencySucceed, (state, { payload }) => {
    return {
      ...state,
      clientConfig: {
        ...state.clientConfig,
        configuration: {
          ...state.clientConfig.configuration,
          search: {
            ...state.clientConfig.configuration.search,
            selectedCurrency: payload,
            isCurrencyInitialize: true,
          },
        },
      },
    };
  }),
  on(localeActions.initializeLocaleSucceed, localeActions.changeLocale, (state, { newLocale }) => ({
    ...state,
    locale: newLocale,
  })),

  on(updateRecentlyBookedHotel, (state, { recentlyBookedHotel }) => {
    return {
      ...state,
      clientConfig: {
        ...state.clientConfig,
        configuration: {
          ...state.clientConfig.configuration,
          search: {
            ...state.clientConfig.configuration.search,
            recentlyBooked: [
              recentlyBookedHotel,
              ...state.clientConfig.configuration.search.recentlyBooked.filter((r) => r.id !== recentlyBookedHotel.id),
            ],
          },
        },
      },
    };
  }),

  on(cidActions.generateCidSucceed, cidActions.readCidSucceed, (state, { guid }) => ({
    ...state,
    cid: guid,
  })),
);

export function configReducer(state: ConfigState | undefined, action: Action) {
  return reducer(state, action);
}
